<template>
	<div>
		<FallBack
			v-if="contactError"
			data-test="show-error"
			iconStyle="fal"
			icon="exclamation-triangle"
			message="We could not load this contact, please try again later."
		/>
		<template v-else>
			<section class="overview-container" data-tour="cc-contact-overview">
				<ProfileOverview
					:leadDetails="leadDetails"
					:mainProfileIsInternalUser="mainProfileIsInternalUser"
				/>
				<QuickActionsMenu
					data-tour="cc-contact-quick-action-button"
					:lightButton="true"
					:lightButtonIcon="['far', 'ellipsis-h']"
					:menus="populateQuickActions"
					@editContact="$emit('editContactClick')"
					@transferContact="$emit('transferContactClick')"
					@deactivateContact="$emit('deactivateContactClick')"
					@sendToBlink="$emit('sendToBlinkClick')"
					@addLeadNote="$emit('addLeadNote')"
					@sendToVendor="$emit('sendToVendor')"
				/>
			</section>
			<section class="tab-container">
				<div
					v-for="(tab, index) in TABS"
					:key="index"
					class="tab"
					@click="handleTabClick(tab.view)"
					:data-tour="`cc-contact-${tab.name.toLowerCase()}`"
				>
					<font-awesome-icon
						class="tab-icon"
						:icon="[`${tab.icon.style}`, `${tab.icon.name}`]"
					/>
					<div>
						<div class="tab-name">{{ tab.name }}</div>
						<div class="tab-description">{{ tab.description }}</div>
					</div>
					<font-awesome-icon
						class="chevron-right-icon"
						:icon="['far', 'chevron-right']"
					/>
				</div>
			</section>
		</template>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import FallBack from '@/components/global/FallBack.vue';
import ProfileOverview from './ProfileOverview.vue';
import QuickActionsMenu from '../QuickActionsMenu.vue';
import { TABS } from './constants';

export default {
	name: 'OverviewModalHome',
	components: {
		FallBack,
		ProfileOverview,
		QuickActionsMenu,
	},
	props: {
		leadDetails: Object,
		blinkDisabled: Boolean,
		loggedInUserId: Number,
	},
	computed: {
		...mapGetters([
			'canDeleteContact',
			'canReassignLeadsToDifferentContact',
		]),
		...mapState({
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
			showVendorLinkQuickAction: (state) => !!state.profile.bigMacLink,
		}),
		populateQuickActions() {
			const menu = [
				[
					{
						icon: ['far', 'pen'],
						text: 'Edit Contact',
						action: 'editContact',
						disabled: false,
					},
					{
						icon: ['far', 'exchange'],
						text: 'Transfer Contact',
						action: 'transferContact',
						disabled:
							this.mainProfileIsInternalUser ||
							!this.canReassignLeadsToDifferentContact,
					},
					{
						icon: ['far', 'file-import'],
						text: 'Send to Blink+',
						action: 'sendToBlink',
						disabled:
							this.mainProfileIsInternalUser ||
							this.blinkDisabled ||
							this.loggedInUserId !== this.leadDetails.contactId,
					},
					{
						icon: ['far', 'sticky-note'],
						text: 'Add Note',
						action: 'addLeadNote',
						disabled: this.mainProfileIsInternalUser,
					},
				],
				[
					{
						icon: ['far', 'trash-alt'],
						text: 'Deactivate Contact',
						action: 'deactivateContact',
						iconColor: 'red',
						disabled:
							this.mainProfileIsInternalUser ||
							!this.canDeleteContact,
					},
				],
			];

			if (this.showVendorLinkQuickAction) {
				menu[0].push({
					icon: ['far', 'file-import'],
					text: 'Send Vendor Link',
					action: 'sendToVendor',
					disabled:
						this.mainProfileIsInternalUser || this.isImpersonated,
				});
			}

			return menu;
		},
		contactError() {
			return Object.hasOwn(this.leadDetails, 'error');
		},
	},
	data() {
		return {
			TABS,
		};
	},
	methods: {
		handleTabClick(view) {
			this.$emit('onTabClick', view);
		},
	},
};
</script>

<style lang="scss" scoped>
.overview-container {
	display: grid;
	grid-template-columns: auto 44px;
	grid-auto-flow: dense;
	@include rem('padding', 20px);
	@include rem('grid-gap', 10px);
}

/deep/ .btn-quick-actions {
	@include rem('width', 44px);
	@include rem('height', 44px);
}

/deep/ .menu__container {
	@include rem('top', 54px);
}

.tab-container {
	display: flex;
	flex-direction: column;

	.tab {
		display: grid;
		align-items: center;
		grid-template-columns: 44px auto 20px;
		border-top: 1px solid $border-color-light;
		@include rem('gap', 16px);
		@include rem('padding', 18px);

		.tab-name {
			font-weight: 500;
			@include rem('font-size', 17px);
			color: $brand-blue;
			cursor: pointer;
		}

		.tab-icon {
			color: $black;
			background-color: $white;
			border: 1px solid $border-color-light;
			border-radius: 6px;
			@include rem('padding', 12px);
			@include rem('font-size', 22px);
			cursor: pointer;
		}

		.tab-description {
			color: $b360-color--gray__darker;
			cursor: pointer;
		}

		.chevron-right-icon {
			@include rem('font-size', 18px);
			color: $black;
			cursor: pointer;
		}
	}
}
</style>
